import { Money } from '@square/dashboard-protos/models/squareup/common/currency';
import { GetCardAtmLimitsResponse } from '@square/dashboard-protos/models/squareup/bbfrontend/dashboardweb/card/card_service';
import formatMoney from '@banking/ui/utils/format-money';
import { getBrowserTimeZone } from '@dashboard/junk-drawer/utils/time';
import * as moment from 'moment-timezone';
const Cadence = GetCardAtmLimitsResponse.Cadence;
export let LimitColor = /*#__PURE__*/function (LimitColor) {
  LimitColor["REACHED"] = "var(--core-red-fill-dark-mode-color)";
  LimitColor["NORMAL"] = "var(--core-blue-fill-dark-mode-color)";
  LimitColor["DISABLED"] = "#D9D9D9";
  return LimitColor;
}({});
export const convertMoney = money => {
  if (!money) {
    throw new Error('undefined money');
  }
  return money;
};
export const calculatePercentage = limit => {
  const limitAmount = convertMoney(limit.limitAmount).amount;
  const availableToSpendAmount = convertMoney(limit.availableToSpendAmount).amount;
  const percentage = (limitAmount - availableToSpendAmount) / limitAmount * 100;
  return `${percentage.toFixed(0)}%`;
};
export const getCadencesResetTimeRecord = (intl, localeTime) => {
  const timezone = getBrowserTimeZone();
  const currentDateUtc = moment.utc();
  const startOfNextDayUtc = moment.utc().add(1, 'day').startOf('day').add(1, 'minute');
  const startOfNextWeekUtc = moment.utc().add(1, 'week').startOf('isoWeek').add(1, 'minute');
  const startOfNextMonthUtc = moment.utc().add(1, 'month').startOf('month').add(1, 'minute');
  return {
    [Cadence.DAILY]: intl.t('banking.checking.cardSettings.atmLimits.daily.reset', {
      time: localeTime.dateTime(startOfNextDayUtc.clone().tz(timezone), 'shortTimeWithZone')
    }),
    [Cadence.WEEKLY]: intl.t('banking.checking.cardSettings.atmLimits.weekly.reset', {
      time: localeTime.dateTime(startOfNextWeekUtc.clone().tz(timezone), 'shortTimeWithZone')
    }),
    [Cadence.MONTHLY]: intl.t('banking.checking.cardSettings.atmLimits.monthly.reset', {
      numOfDays: startOfNextMonthUtc.diff(currentDateUtc, 'days')
    })
  };
};
export const translations = {
  [Cadence.MONTHLY]: 'banking.checking.cardSettings.atmLimits.monthly.limitReached',
  [Cadence.WEEKLY]: 'banking.checking.cardSettings.atmLimits.weekly.limitReached',
  [Cadence.DAILY]: 'banking.checking.cardSettings.atmLimits.daily.limitReached'
};
export const getLimitReachedStyle = (percentage, backgroundColor, limitType, intl) => {
  if (percentage === undefined) {
    throw new Error(`No percentage set for the cadence ${limitType}`);
  }
  const progressBarStyles = `width: ${percentage}; background: ${backgroundColor}`;
  const hasLimitReach = limitType !== null;
  let limitReachedText = undefined;
  if (hasLimitReach) {
    const translationKey = translations[limitType];
    if (!translationKey) {
      throw new Error(`translationKey ${translationKey} key does not exist`);
    }
    limitReachedText = intl.t(translationKey);
  }
  return {
    limitReachedText,
    hasLimitReach,
    progressBarStyles
  };
};
export const getLimitStyles = (limitsPercentageMap, intl) => {
  if (limitsPercentageMap[Cadence.MONTHLY] === '100%') {
    return {
      [Cadence.MONTHLY]: getLimitReachedStyle(limitsPercentageMap[Cadence.MONTHLY], LimitColor.REACHED, Cadence.MONTHLY, intl),
      [Cadence.WEEKLY]: getLimitReachedStyle(limitsPercentageMap[Cadence.WEEKLY], LimitColor.DISABLED, Cadence.MONTHLY, intl),
      [Cadence.DAILY]: getLimitReachedStyle(limitsPercentageMap[Cadence.DAILY], LimitColor.DISABLED, Cadence.MONTHLY, intl)
    };
  }
  if (limitsPercentageMap[Cadence.WEEKLY] === '100%') {
    return {
      [Cadence.MONTHLY]: getLimitReachedStyle(limitsPercentageMap[Cadence.MONTHLY], LimitColor.NORMAL, null, intl),
      [Cadence.WEEKLY]: getLimitReachedStyle(limitsPercentageMap[Cadence.WEEKLY], LimitColor.REACHED, Cadence.WEEKLY, intl),
      [Cadence.DAILY]: getLimitReachedStyle(limitsPercentageMap[Cadence.DAILY], LimitColor.DISABLED, Cadence.WEEKLY, intl)
    };
  }
  if (limitsPercentageMap[Cadence.DAILY] === '100%') {
    return {
      [Cadence.MONTHLY]: getLimitReachedStyle(limitsPercentageMap[Cadence.MONTHLY], LimitColor.NORMAL, null, intl),
      [Cadence.WEEKLY]: getLimitReachedStyle(limitsPercentageMap[Cadence.WEEKLY], LimitColor.NORMAL, null, intl),
      [Cadence.DAILY]: getLimitReachedStyle(limitsPercentageMap[Cadence.DAILY], LimitColor.REACHED, Cadence.DAILY, intl)
    };
  }
  return {
    [Cadence.MONTHLY]: getLimitReachedStyle(limitsPercentageMap[Cadence.MONTHLY], LimitColor.NORMAL, null, intl),
    [Cadence.WEEKLY]: getLimitReachedStyle(limitsPercentageMap[Cadence.WEEKLY], LimitColor.NORMAL, null, intl),
    [Cadence.DAILY]: getLimitReachedStyle(limitsPercentageMap[Cadence.DAILY], LimitColor.NORMAL, null, intl)
  };
};
export const createLimitObject = (cadence, title, limit, resetTime, limitStyle, intl, formatter) => {
  return {
    cadence,
    title,
    limit: formatMoney(convertMoney(limit.limitAmount), formatter, false),
    used: intl.t('banking.checking.cardSettings.atmLimits.used', {
      amount: formatMoney(Money.create({
        amount: convertMoney(limit.limitAmount).amount - convertMoney(limit.availableToSpendAmount).amount,
        currencyCode: convertMoney(limit.limitAmount).currencyCode
      }), formatter, false)
    }),
    remaining: intl.t('banking.checking.cardSettings.atmLimits.remaining', {
      amount: formatMoney(convertMoney(limit.availableToSpendAmount), formatter, false)
    }),
    resetTime,
    percentage: calculatePercentage(limit),
    limitReachedText: limitStyle?.limitReachedText,
    hasLimitReach: limitStyle?.hasLimitReach,
    progressBarStyles: limitStyle?.progressBarStyles
  };
};
export const getLimitsPercentage = limits => {
  return limits.reduce((acc, limit) => {
    switch (limit.cadence) {
      case Cadence.DAILY:
        acc[Cadence.DAILY] = calculatePercentage(limit);
        break;
      case Cadence.WEEKLY:
        acc[Cadence.WEEKLY] = calculatePercentage(limit);
        break;
      case Cadence.MONTHLY:
        acc[Cadence.MONTHLY] = calculatePercentage(limit);
        break;
    }
    return acc;
  }, {});
};
export const createLimits = (limits, intl, formatter, localeTime) => {
  const resetTimeRecord = getCadencesResetTimeRecord(intl, localeTime);
  const limitsPercentageRecord = getLimitsPercentage(limits);
  const limitStyleRecord = getLimitStyles(limitsPercentageRecord, intl);
  return limits.reduce((acc, limit) => {
    switch (limit.cadence) {
      case Cadence.DAILY:
        return {
          ...acc,
          [Cadence.DAILY]: createLimitObject(Cadence.DAILY, intl.t('banking.checking.cardSettings.atmLimits.daily.title'), limit, resetTimeRecord[Cadence.DAILY], limitStyleRecord[Cadence.DAILY], intl, formatter)
        };
      case Cadence.WEEKLY:
        return {
          ...acc,
          [Cadence.WEEKLY]: createLimitObject(Cadence.WEEKLY, intl.t('banking.checking.cardSettings.atmLimits.weekly.title'), limit, resetTimeRecord[Cadence.WEEKLY], limitStyleRecord[Cadence.WEEKLY], intl, formatter)
        };
      case Cadence.MONTHLY:
        return {
          ...acc,
          [Cadence.MONTHLY]: createLimitObject(Cadence.MONTHLY, intl.t('banking.checking.cardSettings.atmLimits.monthly.title'), limit, resetTimeRecord[Cadence.MONTHLY], limitStyleRecord[Cadence.MONTHLY], intl, formatter)
        };
      case Cadence.PER_TRANSACTION:
        return {
          ...acc,
          [Cadence.PER_TRANSACTION]: createLimitObject(Cadence.PER_TRANSACTION, intl.t('banking.checking.cardSettings.atmLimits.transaction.title'), limit, '', undefined, intl, formatter)
        };
      default:
        return acc;
    }
  }, {
    [Cadence.DAILY]: {},
    [Cadence.WEEKLY]: {},
    [Cadence.MONTHLY]: {},
    [Cadence.PER_TRANSACTION]: {}
  });
};